"use client"

import { useState, useEffect } from "react"
import { motion } from "framer-motion"
import { Link } from "react-router-dom"
import { toast, ToastContainer } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"
import axios from "axios"
import config from "../../config"
import { usePageSave } from "../../contexts/PageSaveContext"
import { useServerPageStatus } from "../../contexts/ServerPageStatusContext"
import ProjectLevelJSONUploader from "./ProjectLevelJSONUploader"
import { FileJson, Calculator } from "lucide-react"

const formatCurrency = (value) => {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
  }).format(value || 0)
}

const TaxComparison = () => {
  const { areAllPagesSaved, resetAllPages, isDeductionValidationRequired, getDeductionValidationMessage } = usePageSave()
  const { refreshAllPageStatuses } = useServerPageStatus()
  const [selectedRegime, setSelectedRegime] = useState(() => {
    // Get saved regime from localStorage, default to "new" if not found
    return localStorage.getItem("selectedTaxRegime") || "new"
  })
  const [dateOfFiling, setDateOfFiling] = useState("12-08-2025")
  const [loading, setLoading] = useState(true)
  const [oldRegimeData, setOldRegimeData] = useState(null)
  const [newRegimeData, setNewRegimeData] = useState(null)
  const [fileInputRef, setFileInputRef] = useState(null)
  const [deductionValidation, setDeductionValidation] = useState(null)

  console.log(oldRegimeData, newRegimeData)

  // Handle regime selection change and save to localStorage
  const handleRegimeChange = (regime) => {
    setSelectedRegime(regime)
    localStorage.setItem("selectedTaxRegime", regime)
  }


  // Retrieve data from localStorage
  const panNo = typeof window !== "undefined" ? localStorage.getItem("PAN_NO") : null
  const year = typeof window !== "undefined" ? localStorage.getItem("YEAR") : null
  const dob = typeof window !== "undefined" ? localStorage.getItem("DOB") : null

  // Fetch tax computation data
  useEffect(() => {
    const fetchTaxComputation = async () => {
      try {
        if (panNo && year) {
          const [oldRes, newRes] = await Promise.all([
            axios.get(`${config.apiUrl}/user/tax-computation/${panNo}/${year}/OLD`),
            axios.get(`${config.apiUrl}/user/tax-computation/${panNo}/${year}/NEW`)
          ]);

          setOldRegimeData(oldRes.data?.taxComputation)
          setNewRegimeData(newRes.data?.taxComputation)
          setLoading(false)
        }
      } catch (error) {
        console.error("Error fetching tax computation:", error)
        toast.error("Failed to load tax computation data")
      } finally {
        setLoading(false)
      }
    }

    fetchTaxComputation()
  }, [panNo, year])

  // Check deduction validation
  useEffect(() => {
    const checkDeductionValidation = async () => {
      try {
        const validation = await isDeductionValidationRequired()
        setDeductionValidation(validation)
      } catch (error) {
        console.error('Error checking deduction validation:', error)
        setDeductionValidation({ required: false, reason: null, type: null })
      }
    }

    checkDeductionValidation()
    
    // Check every 30 seconds to keep validation up to date
    const interval = setInterval(checkDeductionValidation, 30000)
    
    return () => clearInterval(interval)
  }, [isDeductionValidationRequired])

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-500"></div>
      </div>
    )
  }


  // Prepare tax data for display
  const taxData = [
    {
      label: "Income From Salary",
      oldRegime: formatCurrency(oldRegimeData?.incomeFromSalary) || 0,
      newRegime: formatCurrency(newRegimeData?.incomeFromSalary) || 0,
      link: "/dashboard/salary",
    },
    {
      label: "Income From House Property",
      oldRegime: formatCurrency(oldRegimeData?.incomeFromHP) || 0,
      newRegime: formatCurrency(newRegimeData?.incomeFromHP) || 0,
      link: "/dashboard/house-property",
    },
    {
      label: "Income From Other Sources",
      oldRegime: formatCurrency(oldRegimeData?.incomeFromOtherSources) || 0,
      newRegime: formatCurrency(newRegimeData?.incomeFromOtherSources) || 0,
      link: "/dashboard/other-sources",
    },
    {
      label: "Total Income",
      oldRegime: formatCurrency(
        oldRegimeData?.incomeFromSalary +
        oldRegimeData?.incomeFromHP +
        oldRegimeData?.incomeFromOtherSources
      ) || 0,
      newRegime: formatCurrency(
        newRegimeData?.incomeFromSalary +
        newRegimeData?.incomeFromHP +
        newRegimeData?.incomeFromOtherSources
      ) || 0,
      link: "#",
    },
    {
      label: "Less: Deductions VI-A",
      oldRegime: formatCurrency(oldRegimeData?.totalDeductionVia) || 0,
      newRegime: "₹0.00",
      link: "/dashboard/deduction-vi-a",
    },
    {
      label: "Total Taxable Income",
      oldRegime: formatCurrency(oldRegimeData?.totalTaxableIncome) || 0,
      newRegime: formatCurrency(newRegimeData?.totalTaxableIncome) || 0,
      link: "#",
    },
    {
      label: "Tax Payable",
      oldRegime: formatCurrency(oldRegimeData?.grossTaxLiability) || 0,
      newRegime: formatCurrency(newRegimeData?.grossTaxLiability) || 0,
      link: "#",
    },
    {
      label: "Less: TDS/TCS",
      oldRegime: formatCurrency(oldRegimeData?.totalTDS + oldRegimeData?.totalTCS) || 0,
      newRegime: formatCurrency(newRegimeData?.totalTDS + newRegimeData?.totalTCS) || 0,
      link: "#"
    },
    {
      label: "Interest U/S 234 A/B/C",
      oldRegime: formatCurrency(
        oldRegimeData?.interestPayable234A +
        oldRegimeData?.interestPayable234B +
        oldRegimeData?.interestPayable234C
      ) || 0,
      newRegime: formatCurrency(
        newRegimeData?.interestPayable234A +
        newRegimeData?.interestPayable234B +
        newRegimeData?.interestPayable234C
      ) || 0,
      link: "#",
    },
    {
      label: "Tax to be Paid",
      oldRegime: formatCurrency(oldRegimeData?.netTaxPayable) || 0,
      newRegime: formatCurrency(newRegimeData?.netTaxPayable) || 0,
      link: "#",
    },
  ]

  const downloadJson = async () => {
    try {
      const response = await axios.get(`${config.apiUrl}/itr/itr1/${panNo}/${year}`)
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: "application/json" })
      const url = URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.download = `${panNo}_${year}.json`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
      toast.success("Downloading JSON file")
    } catch (error) {
      toast.error("Error downloading JSON file")
      console.log("Error downloading JSON:", error)
    }
  }

  const handleSubmit = async(selectedRegime)=>{
    try {
      const res = await axios.get(`${config.apiUrl}/user/fillingsection/${panNo}/${year}`)
      const fillingSectionData = res.data || null
      if(fillingSectionData.fillingSection){
        fillingSectionData.fillingSection.optOutNewTaxRegime = selectedRegime
        await axios.patch(`${config.apiUrl}/user/fillingsection/${panNo}/${year}`,fillingSectionData)
        await downloadJson()
      }

    } catch (error) {
      console.log(`Error in handleSubmit`, error)
    }
  }

  const handleUploadClick = () => {
    if (fileInputRef) {
      fileInputRef.click()
    }
  }

  const handleFileChange = (event) => {
    const file = event.target.files?.[0]
    if (file) {
      // Trigger the upload in the ProjectLevelJSONUploader component
      setTimeout(() => {
        const uploaderComponent = document.querySelector('[data-uploader="project-level"]')
        if (uploaderComponent) {
          const fileInput = uploaderComponent.querySelector('input[type="file"]')
          if (fileInput) {
            const dataTransfer = new DataTransfer()
            dataTransfer.items.add(file)
            fileInput.files = dataTransfer.files
            fileInput.dispatchEvent(new Event('change', { bubbles: true }))
          }
        }
      }, 100)
    }
  }

  return (
    <div className="bg-white rounded-xl shadow-md border">
      <ToastContainer />
      
      {/* Upload PreFill Data Section - Simplified */}
      <div className="p-3 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-2">
            <button
              onClick={handleUploadClick}
              className="px-3 py-1.5 bg-green-500 text-white text-xs rounded-md hover:bg-green-600 transition-colors flex items-center space-x-1.5"
              title="Upload JSON for all data (Profile, Tax Paid, TDS, TCS)"
            >
              <FileJson className="w-3.5 h-3.5" />
              <span>Upload PreFill Data</span>
            </button>
          </div>
        </div>
        
        {/* Hidden file input */}
        <input
          ref={setFileInputRef}
          type="file"
          accept=".json"
          onChange={handleFileChange}
          className="hidden"
        />
        
        {/* Uploader component - visible for messages but interface hidden */}
        <div data-uploader="project-level">
          <ProjectLevelJSONUploader isVisible={true} hideUploadInterface={true} />
        </div>
      </div>
      
      <div className="grid grid-cols-3 gap-0 p-0">
        {/* Details Column Header */}
        <div className="font-semibold text-gray-700 flex items-center justify-start bg-cyan-200 px-4 py-3 border-r border-cyan-300">
          Income Details
        </div>

        {/* Old Regime Column Header */}
        <div className="font-semibold text-gray-700 flex items-center justify-center bg-cyan-200 px-4 py-3 border-r border-cyan-300">
          <div className="flex items-center gap-3">
            <input
              type="radio"
              name="regime"
              value="old"
              checked={selectedRegime === "old"}
              onChange={(e) => handleRegimeChange(e.target.value)}
              className="w-4 h-4 text-cyan-600 focus:ring-cyan-500"
            />
            <span>Old Regime</span>
          </div>
        </div>

        {/* New Regime Column Header */}
        <div className="font-semibold text-gray-700 flex items-center justify-center bg-cyan-200 px-4 py-3">
          <div className="flex items-center gap-3">
            <input
              type="radio"
              name="regime"
              value="new"
              checked={selectedRegime === "new"}
              onChange={(e) => handleRegimeChange(e.target.value)}
              className="w-4 h-4 text-cyan-600 focus:ring-cyan-500"
            />
            <span>New Regime</span>
          </div>
        </div>

        {/* Table Rows */}
        {taxData.map((item, index) => (
          <motion.div
            key={item.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.1 }}
            className="contents"
          >
            <div
              className={`py-4 text-sm text-left px-4 border-r border-cyan-300 ${
                index === taxData.length - 1 
                  ? "bg-cyan-200 font-bold text-gray-800" 
                  : index % 2 === 0 
                    ? "bg-white text-gray-700" 
                    : "bg-cyan-50 text-gray-700"
              }`}
            >
              <Link 
                className={`${item.link !== "#" ? "hover:text-cyan-600 transition-colors duration-200" : "font-bold"}`} 
                href={item.link}
              >
                {item.label}
              </Link>
            </div>
            <div
              className={`py-4 text-sm text-right px-4 border-r border-cyan-300 ${
                index === taxData.length - 1 
                  ? "bg-cyan-200 font-bold text-gray-800" 
                  : index % 2 === 0 
                    ? "bg-white text-gray-700" 
                    : "bg-cyan-50 text-gray-700"
              }`}
            >
              {item.oldRegime}
            </div>
            <div
              className={`py-4 text-sm text-right px-4 ${
                index === taxData.length - 1 
                  ? "bg-cyan-200 font-bold text-gray-800" 
                  : index % 2 === 0 
                    ? "bg-white text-gray-700" 
                    : "bg-cyan-50 text-gray-700"
              }`}
            >
              {item.newRegime}
            </div>
          </motion.div>
        ))}
      </div>


      {/* Action Buttons */}
      <div className="p-3 border-t border-gray-200 bg-gray-50">
        <div className="flex justify-end items-center gap-4">
          {/* Right side buttons */}
          <div className="flex flex-col items-end gap-2">
            {!areAllPagesSaved() && (
              <p className="text-xs text-red-600 font-medium">
                Please save Profile, Salary, and Filing Section before submitting
              </p>
            )}
            {deductionValidation?.required && (
              <div className="text-xs text-red-600 font-medium max-w-xs text-right">
                <p>{deductionValidation.reason}</p>
                {deductionValidation.type === 'api_mismatch' && (
                  <p className="text-xs text-gray-500 mt-1">
                    Data on server differs from local data. Please save deduction page to sync.
                  </p>
                )}
              </div>
            )}
            <button
              onClick={() => {handleSubmit(selectedRegime.toUpperCase())}}
              disabled={!areAllPagesSaved() || (deductionValidation?.required)}
              className={`px-4 py-2 rounded-md font-medium text-xs transition-all duration-200 ${
                areAllPagesSaved() && !deductionValidation?.required
                  ? "bg-cyan-600 text-white hover:bg-cyan-700 hover:shadow-lg transform hover:-translate-y-0.5" 
                  : "bg-gray-400 text-gray-200 cursor-not-allowed"
              }`}
            >
              <div className="flex flex-col items-center">
                <span>Download JSON</span>
                <span className="text-xs font-normal opacity-90">
                  ({selectedRegime === "old" ? "Old" : "New"} Regime)
                </span>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TaxComparison